import * as React from 'react';

type UseHasScrolledPastElOptions = {
    scrollContainerEl: HTMLDivElement | null;
    targetEl: HTMLDivElement | null;
    offset?: number;
};

/**
 * This hook is used to determine if a scroll container has scrolled a particular
 * distance, usually based on the height of some other element. It will automatically
 * calculate (and recalculate) the height of the target element when the window
 * resized, or when the target content changes.
 *
 * Example usage:
 *
 * const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
 * const targetElRef = React.useRef<HTMLDivElement | null>(null);
 * const hasScrolledPastCheckpoint = useHasScrolledPastEl({scrollContainerRef, targetEl});
 */
export function useHasScrolledPastEl(options: UseHasScrolledPastElOptions): boolean {
    const {scrollContainerEl, targetEl, offset = 0} = options;
    const [hasScrolledPastCheckpoint, setHasScrolledPastCheckpoint] = React.useState(false);
    const [scrollCheckpoint, setScrollCheckpoint] = React.useState<number | null | undefined>(null);
    const checkpointRef = React.useRef<number | null | undefined>(null);

    React.useEffect(() => {
        checkpointRef.current = scrollCheckpoint;
    }, [scrollCheckpoint]);

    // Handle scroll checkpoint calculation
    React.useEffect(() => {
        const updateCheckpoint = () => {
            if (!targetEl || !scrollContainerEl) {
                setScrollCheckpoint(null);

                return;
            }

            requestAnimationFrame(() => {
                const targetRect = targetEl.getBoundingClientRect();
                const containerRect = scrollContainerEl.getBoundingClientRect();

                // Calculate where the target is positioned within the scroll container
                const targetOffsetFromContainerTop =
                    targetRect.top - containerRect.top + scrollContainerEl.scrollTop;

                setScrollCheckpoint(targetOffsetFromContainerTop + targetRect.height + offset);
            });
        };

        // Initial calculation
        updateCheckpoint();

        // Observers to recalc when target changes size/content
        // @ts-expect-error
        let targetResizeObserver;
        if (window.ResizeObserver && targetEl) {
            targetResizeObserver = new ResizeObserver(updateCheckpoint);
            targetResizeObserver.observe(targetEl);
        }

        // Observer for scroll container width changes
        // @ts-expect-error
        let scrollContainerResizeObserver;
        if (window.ResizeObserver && scrollContainerEl) {
            scrollContainerResizeObserver = new ResizeObserver(updateCheckpoint);
            scrollContainerResizeObserver.observe(scrollContainerEl);
        }

        // @ts-expect-error
        let mutationObserver;
        if (window.MutationObserver && targetEl) {
            mutationObserver = new MutationObserver(() => {
                setTimeout(updateCheckpoint, 10);
            });
            mutationObserver.observe(targetEl, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
            });
        }

        return () => {
            // @ts-expect-error
            if (targetResizeObserver) targetResizeObserver.disconnect();
            // @ts-expect-error
            if (scrollContainerResizeObserver) scrollContainerResizeObserver.disconnect();
            // @ts-expect-error
            if (mutationObserver) mutationObserver.disconnect();
        };
    }, [targetEl, scrollContainerEl, offset]);

    // Scroll detection + initial sync
    React.useEffect(() => {
        const el = scrollContainerEl;
        if (!el) {
            setHasScrolledPastCheckpoint(false);

            return;
        }

        const handleScroll = () => {
            const checkpoint = checkpointRef.current;
            if (checkpoint == null) {
                setHasScrolledPastCheckpoint(false);

                return;
            }
            const scrolledPast = el.scrollTop > checkpoint;
            setHasScrolledPastCheckpoint(scrolledPast);
        };

        el.addEventListener('scroll', handleScroll, {passive: true});

        // Sync immediately and after paint
        handleScroll();
        const rafId = requestAnimationFrame(handleScroll);

        return () => {
            el.removeEventListener('scroll', handleScroll);
            cancelAnimationFrame(rafId);
        };
    }, [scrollContainerEl, scrollCheckpoint]);

    return hasScrolledPastCheckpoint;
}
